// ---- floating letters ----
// Canvas 2D shapes the isolated Arabic form exactly as an SVG <text> would;
// three.js only ever sees the bitmap. That is the whole Arabic answer — what is
// not available is 3D type, since TextGeometry does no shaping at all.
import * as THREE from 'three';
import {colOf,facesOf,inkFit,markFit,scriptOf,unitsOf} from './letters';
import {cam,placeCam,pxPerUnit,scene,T,view} from './scene';
import {slotRect,slotSize} from './tray';
import {BEAT,clamp,rnd,smooth} from './util';

// px is how wide the chip is on the screen, in pixels: the size it is drawn at
// wherever it is, and the size a tap is judged against. wi is its place in the
// word — the tray slot it rises out of when dealt — and slot its place in the sky.
// ch is the letter, which is what it is named by; face is what is drawn on it,
// the letter with the حركة it carries in this word (facesOf, letters.ts).
export interface Chip extends THREE.Sprite{ch:string;face:string;wi:number;slot:number;n:number;px:number;born:number;ph:number;wobT:number;popT:number;dying:number;p0:THREE.Vector3;from:THREE.Vector3}
interface Flyer{s:Chip;i:number;from:THREE.Vector3;s0:number;t0:number}
const chipTexs=new Map<string,THREE.CanvasTexture>();
function chipTex(ch:string){
  const had=chipTexs.get(ch); if(had)return had;
  const cv=document.createElement('canvas'); cv.width=cv.height=256;
  const c=cv.getContext('2d')!, col=colOf(ch);
  c.fillStyle='#fff'; c.beginPath(); c.arc(128,128,112,0,6.29); c.fill();
  c.lineWidth=12; c.strokeStyle=col; c.stroke();
  c.globalAlpha=0.10; c.fillStyle=col; c.beginPath(); c.arc(128,128,96,0,6.29); c.fill(); c.globalAlpha=1;
  const s=scriptOf(); c.textAlign='center'; c.fillStyle=col;
  if(!s.box){                                   // ي hangs low; +12 centres it by eye, and a mark that would leave the disc is brought in
    const f=markFit(ch); c.font='bold '+(150*f.k)+'px '+s.font; c.textBaseline='middle'; c.fillText(ch,128,140+f.dy*150*f.k);
  }
  else{                                         // an akshara: shrunk to fit, and centred on its own ink
    c.font='bold '+Math.floor(150*inkFit(ch))+'px '+s.font; c.textBaseline='alphabetic';
    const m=c.measureText(ch);
    c.fillText(ch,128+(m.actualBoundingBoxLeft-m.actualBoundingBoxRight)/2,128+(m.actualBoundingBoxAscent-m.actualBoundingBoxDescent)/2);
  }
  const t=new THREE.CanvasTexture(cv); t.colorSpace=THREE.SRGBColorSpace; chipTexs.set(ch,t); return t;
}
// Where an n-letter word's chips go and how big they are, in pixels. They are
// the thing to tap, so each is 1.3× the tray slot it will fill — the slot is
// sized to the width (slotSize) and a floor keeps a long word's lean slots from
// shrinking the chips with them. They sit in a row across the top of the frame,
// under the home button and the badge, alternate letters raised into a second
// row so a long word's neighbours clear each other, and spread wider the more
// there are. Sizing them in world units, as they were, made them a fraction of
// the screen's *height*: on a phone on its side that was half the slot.
function layout(n:number){
  const W=innerWidth, H=innerHeight;
  const d=Math.max(1.3*slotSize(n),Math.min(0.10*Math.max(W,H),108));
  const spread=Math.min(W-d-16,W*Math.min(0.86,0.62+0.06*Math.max(0,n-3))), gap=n>1?spread/(n-1):W;
  // the second row sits a little below the first — further when the row is
  // tight, so that neighbours a chip apart still clear each other
  const zig=Math.min(0.85*d,Math.max(Math.min(0.7*d,0.07*H),Math.sqrt(Math.max(0,(1.06*d)**2-gap*gap))));
  const top=Math.max(0.23*H,66+d/2);            // under the home button and the badge, drift included
  const up=(i:number)=>(i-((n-1)>>1))%2===0;    // the middle letter is in the upper row, clear of Jiddo's head
  return {d,x:(i:number)=>n>1?(W-spread)/2+gap*i:W/2,y:(i:number)=>top+(up(i)?0:zig)};
}
// Chips live on a plane three units behind Jiddo, square to the camera's
// heading (never between the camera and an animal), at those screen spots, so
// both orientations always show every letter, evenly spread — no push-apart
// code needed. The plane turns with the player: orbit the camera and the
// letters come round with it, easing into place — and follows Jiddo with it.
const BAND=new THREE.Plane(), BN=new THREE.Vector3(), BP=new THREE.Vector3();
const ray=new THREE.Raycaster(), v2=new THREE.Vector2(), v3=new THREE.Vector3(), tmp=new THREE.Vector3();
export let chips:Chip[]=[];
let flyers:Flyer[]=[];
// the chip's spot (p0) and size (px), read off the screen
function place(c:Chip){
  const L=layout(c.n); c.px=L.d;
  placeCam(view.yaw,0.28);                      // at the home pitch, whichever way the player has turned
  BN.set(Math.sin(view.yaw),0,Math.cos(view.yaw));        // toward the camera
  BAND.setFromNormalAndCoplanarPoint(BN,BP.copy(T).addScaledVector(BN,-3));
  ray.setFromCamera(v2.set(L.x(c.slot)/innerWidth*2-1,1-L.y(c.slot)/innerHeight*2),cam);
  ray.ray.intersectPlane(BAND,c.p0); placeCam(view.yaw,view.el);
}
// the world size that draws the chip px pixels across where it is: its depth
// from the camera, times the pixels a unit is at depth one
function sizeOf(c:Chip){return c.px*-tmp.copy(c.position).applyMatrix4(cam.matrixWorldInverse).z/pxPerUnit();}
// The word's letters into the sky, shuffled: each rises out of its own slot in
// the tray, one after another in reading order, to the spot it will float at
// (updateChips). Whatever was up there fades out. A letter is whatever the
// script says it is (unitsOf, letters.ts): a character in Arabic, a syllable in Hindi.
// The sky holds this word's letters and no others, so each wears the mark it has
// in the word, as its slot in the tray does.
export function deal(word:string,t:number){
  for(const c of chips)c.dying=t;
  const u=unitsOf(word), f=facesOf(word), n=u.length, order=u.map((_,i)=>i);
  for(let i=n-1;i>0;i--){const j=(Math.random()*(i+1))|0;[order[i],order[j]]=[order[j],order[i]];}
  order.forEach((wi,slot)=>{
    const m=new THREE.SpriteMaterial({map:chipTex(f[wi]),transparent:true,depthTest:false,depthWrite:false,fog:false});
    const s=new THREE.Sprite(m) as Chip; s.renderOrder=10;   // keys sit on top of everything
    Object.assign(s,{ch:u[wi],face:f[wi],wi,slot,n,px:0,born:t+wi*110,ph:rnd(0,6.28),wobT:-1e9,popT:-1e9,dying:0,p0:new THREE.Vector3(),from:new THREE.Vector3()});
    place(s); slotPoint(wi,s.from); s.position.copy(s.from); s.scale.setScalar(0.01);
    scene.add(s); chips.push(s);
  });
}
// picked in screen space with the 2D's 1.25x forgiveness plus a fat-finger halo,
// and before anything 3D, so a chip drifting over Jiddo's head always wins
export function hitChip(px:number,py:number){
  let best:Chip|null=null,bd=1e9;
  for(const c of chips){
    if(c.dying)continue;
    v3.copy(c.position).project(cam);
    const sx=(v3.x+1)/2*innerWidth, sy=(1-v3.y)/2*innerHeight;
    const d=Math.hypot(px-sx,py-sy);
    if(d<c.px/2*1.25+18&&d<bd){bd=d;best=c;}
  }
  return best;
}
// Where slot i is, as a point 3.5 units in front of the camera; returns the
// slot's height on the screen, in pixels — 0, leaving `out` alone, while a
// hidden tab reports no screen to read it off, since a NaN written into a
// position is lerped from ever after.
function slotPoint(i:number,out:THREE.Vector3){
  const r=slotRect(i);
  if(!innerWidth||!innerHeight||!r.height)return 0;
  v3.set((r.left+r.width/2)/innerWidth*2-1,1-(r.top+r.height/2)/innerHeight*2,0.5).unproject(cam);
  out.copy(cam.position).addScaledVector(v3.sub(cam.position).normalize(),3.5);
  return r.height;
}
const atSlot=(h:number)=>h/innerHeight*7*Math.tan(cam.fov*Math.PI/360);   // the world size that draws h pixels at that point
// after a resize: every chip's spot and size, read off the new screen
export function respread(){for(const c of chips)if(!c.dying)place(c);}
// a tapped chip leaves the sky for slot i
export function launch(c:Chip,i:number,t:number){chips=chips.filter(x=>x!==c);flyers.push({s:c,i,from:c.position.clone(),s0:c.scale.x,t0:t});}
// the chips' frame: the dying fade, a dealt one rises out of its slot, the rest
// drift about their spots, shake off a tap out of turn, pop for one in turn,
// and the one he needs breathes when nudged
const RISE=700;                                 // ms from the tray to the sky
export function updateChips(t:number,dt:number,need:string|null,nudge:boolean){
  chips=chips.filter(c=>{
    if(c.dying){const a=(t-c.dying)/240;if(a>=1){scene.remove(c);c.material.dispose();return false;}
      c.material.opacity=1-a;c.scale.setScalar(sizeOf(c)*(1-a*0.55));return true;}
    place(c);                                   // follows the camera round; the lerp below is the ease
    tmp.copy(c.p0); tmp.x+=0.10*Math.sin(t*0.0011+c.ph); tmp.y+=0.14*Math.sin(t*0.0008+c.ph);
    const wa=t-c.wobT, pk=t-c.popT, k=clamp((t-c.born)/RISE,0,1);
    c.material.rotation=wa<520?Math.sin(wa*0.045)*0.19*Math.exp(-wa/210):0;     // shakes off a tap out of turn
    const pop=pk<520?1+0.35*Math.exp(-pk/150):1, glow=(nudge&&c.face===need)?1+0.14*BEAT(t):1;
    const h=k<1?slotPoint(c.wi,c.from):0;      // the slot it is leaving, read fresh so a drag mid-rise still leaves from it; 0 once it is up
    if(h){                                      // still in its slot, or leaping out of it
      const e=1-(1-k)**3;
      c.position.lerpVectors(c.from,tmp,e); c.position.y+=0.4*Math.sin(Math.PI*e);
      c.scale.setScalar(t<c.born?0.01:sizeOf(c)*(h+(c.px-h)*e)/c.px*pop*glow);   // the slot's size leaving, its own arriving
    }else{
      c.position.lerp(tmp,1-Math.exp(-dt*10));
      c.scale.setScalar(sizeOf(c)*pop*glow);
    }
    return true;
  });
}
// a tapped chip flies to its slot; the slot is read fresh every frame so a drag
// mid-flight still lands it. `land` is told the slot as each one arrives.
export function updateFlyers(t:number,land:(i:number)=>void){
  flyers=flyers.filter(f=>{
    const k=Math.min(1,(t-f.t0)/380), e=smooth(k), end=atSlot(slotPoint(f.i,tmp));
    f.s.position.lerpVectors(f.from,tmp,e); f.s.position.y+=0.4*Math.sin(Math.PI*e);
    f.s.scale.setScalar(f.s0+(end-f.s0)*e);
    if(k<1)return true;
    scene.remove(f.s); f.s.material.dispose(); land(f.i);
    return false;
  });
}
